import React, {PureComponent} from "react";
import {connect, DispatchProp} from "react-redux";
import {SceneryType} from "../../store/settings/SceneryType";
import {cssHelper} from "../../shared/utils/cssHelper";
import {range} from "../../shared/utils/range";
import {IApplicationState} from "../../store";
import {leaveCardsGame, selectCard} from "../../store/mini-games/cards/actions";
import {BonusType} from "../../store/player/types";
import {ICardsMiniGameState} from "../../store/mini-games/cards/types";
import {toCssClassName} from "../../shared/utils/toCssClassName";
import styles from "./cards-mini-game.module.css";
import minigameStyles from "../../shared/styles/minigame.module.css";
import bonusStyles from "../../shared/styles/bonuses.module.css";
import {Back, Power4, TimelineLite, TweenLite, TweenMax, Animation, Tween} from "gsap";
import {IAnimationSettings} from "../../store/settings/types";
import playBackground from "../../shared/sounds/playBackground";
import {Random} from "../../shared/utils/random";
import renderText from "../../shared/components/renderText";
import playSound from "../../shared/sounds/playSound";
import getFontSize from "../../shared/utils/getFontSize";
import i18n from "../../shared/i18n";

interface CardsMiniGameSceneProps extends ICardsMiniGameState {
    levelScenery: SceneryType;
    cardCount: number;
    cardsWinRate: number;
    cardBonuses: BonusType[];
    randomSeed: number;
    animation: IAnimationSettings;
}

interface CardsMiniGameState {
    enabled: boolean;
    bonus: BonusType;
    bonuses: BonusType[];
}

type Card = {
    i: number;
    x: number;
    y: number;
    dx: number;
    dy: number;
    rotation: number;
    node: HTMLElement;
};

class CardsMiniGameScene extends PureComponent<CardsMiniGameSceneProps & DispatchProp, CardsMiniGameState> {
    private readonly random: Random;
    private readonly header: React.RefObject<HTMLDivElement>;
    private readonly footer: React.RefObject<HTMLDivElement>;
    private readonly cards: React.RefObject<HTMLDivElement>;
    private readonly activeTweens: Animation[] = [];
    private readonly isIE: boolean;

    constructor(props: CardsMiniGameSceneProps & DispatchProp, context: any) {
        super(props, context);

        this.header = React.createRef();
        this.footer = React.createRef();
        this.cards = React.createRef();

        this.random = new Random(this.props.randomSeed);

        this.state = {
            enabled: false,
            bonus: BonusType.None,
            bonuses: this.getGameBonuses()
        };

        this.isIE = window.navigator.userAgent.indexOf("MSIE ") > -1
            || window.navigator.userAgent.indexOf("Trident/") > -1;
    }

    render() {
        const {levelScenery, isRevealed} = this.props;
        const cards = this.renderCards();

        return (
            <section className={cssHelper(minigameStyles.container).add(`${levelScenery}_cards_bg`).toString()} onClick={isRevealed ? () => this.close() : undefined}>
                <header className={minigameStyles.caption} ref={this.header}>
                    {renderText(i18n("gameplay.cardsminigame.pickcard"), 55, 70, 12, 22)}
                </header>
                <div className={styles.cards} ref={this.cards}>
                    {cards}
                </div>
                <footer className={cssHelper(minigameStyles.footer).add(minigameStyles.footer_revealed, this.props.isRevealed).toString()} ref={this.footer}>
                    {renderText(i18n("gameplay.cardsminigame.taptocontinue"), 36, 55, 15, 30)}
                </footer>
            </section>
        );
    }

    componentDidMount(): void {
        playBackground('bonus-game');

        if (!this.cards.current)
            return;

        let cards = (Array.from(this.cards.current.querySelectorAll('[data-index]')) as HTMLElement[])
            .map((card, i) => {
                TweenLite.set(card!, { x: 0 });

                return {
                    i,
                    x: card!.offsetLeft,
                    y: card!.offsetTop,
                    dx: i === 3
                        ? 25
                        : i === 5
                            ? -25
                            : 0,
                    dy: i % 3 === 1 ? 0 : 50,
                    rotation: ((i % 3) - 1) * 15,
                    node: card!,
                };
            });

        const nodes = cards.map(card => card.node);
        const {animation} = this.props;

        if (!animation.enabled) {
            for (let card of cards) {
                TweenLite.set(card.node, {
                    rotation: card.rotation,
                    x: card.dx,
                    y: card.dy,
                    rotationY: 180
                });

                this.fixIe(card.node, false);
            }

            this.setState({
                enabled: true
            });

            return;
        }

        const timeLine =  new TimelineLite();

        timeLine.timeScale(animation.timeScale);

        timeLine.staggerFromTo(nodes, 0.33, {
            x: -100,
            y: 400,
            rotation: 0,
            autoAlpha: 0,
        }, {
            cycle: {
                rotation: (i: number) => cards[i].rotation,
                x: (i: number) => cards[i].dx,
                y: (i: number) => cards[i].dy,
            },
            autoAlpha: 1,
            onStart: () => playSound(Math.random() > 0.5 ? "card-deal1" : "card-deal2", 0.75)
        }, 0.15);

        timeLine.staggerTo(nodes, 0.33, {
            rotationY: 180,
            onStart: (tween: Tween) => {
                playSound("minigame-card-flip", 0.4);

                this.fixIe(tween.target, false);
            },
            onStartParams:["{self}"]
        }, 0.15);


        timeLine.call(() => {
            this.reshuffleCards(cards, 1, 5, animation.timeScale);
            playSound("cards-shuffle");
        });

        if (this.header.current) {
            timeLine.from(this.header.current, 0.33, {
                autoAlpha: 0,
                scale: 0.7,
                delay: 0.66,
                ease: Back.easeOut.config(5)
            });
        }

        timeLine.call(() => {
            this.setState({
                enabled: true
            });
        });

        this.activeTweens.push(timeLine);
    }

    componentDidUpdate(prevProps: Readonly<CardsMiniGameSceneProps & DispatchProp>, prevState: Readonly<{ enabled: boolean }>, snapshot?: any): void {
        if (this.props.isRevealed && !prevProps.isRevealed) {
            const {animation, cardIndex} = this.props;
            const cards = (Array.from(this.cards.current!.querySelectorAll('[data-index]')) as HTMLElement[]);

            if (animation.enabled) {
                const rotation = (cards[cardIndex] as any)._gsTransform.rotation;

                const timeLine = new TimelineLite();

                timeLine.staggerTo(cards, 0.33, {
                    rotationY: 0,
                    cycle: {
                        delay: (i: number) => i === cardIndex ? 0 : (i + 4) * 0.15,
                    },
                    onStart: (tween: Tween) => {
                        playSound("reveal-map-cards-flip", 0.4);

                        this.fixIe(tween.target, true);
                    },
                    onStartParams:["{self}"]
                }, 0);


                timeLine.call(() => {
                    if (this.header.current) {
                        const timeLine = new TimelineLite();

                        timeLine.to(this.header.current, 0.33, {
                            autoAlpha: 0,
                            scale: 0.7
                        });

                        timeLine.call(() => {
                            this.updateHeaderText();
                        });

                        timeLine.to(this.header.current, 0.33, {
                            autoAlpha: 1,
                            scale: 1,
                            ease: Back.easeOut.config(5)
                        });

                        timeLine.timeScale(animation.timeScale);

                        this.activeTweens.push(timeLine);
                    }

                    if (this.footer.current) {
                        const tween = TweenMax.to(this.footer.current, 1.33,{
                            autoAlpha: 1,
                            repeat: -1,
                            yoyo: true,
                            ease: Power4.easeInOut
                        }).timeScale(animation.timeScale);

                        this.activeTweens.push(tween);
                    }
                });

                timeLine.call(() => {
                    let tween = TweenMax.to(cards[cardIndex], 0.5,{ scale: 1.2 })
                        .timeScale(animation.timeScale);

                    this.activeTweens.push(tween);

                    tween = TweenMax.to(cards[cardIndex], 2,{
                        rotation: rotation + 7,
                        repeat: -1,
                        yoyo: true,
                        ease: Power4.easeInOut
                    }).timeScale(animation.timeScale);

                    this.activeTweens.push(tween);
                });

                timeLine.timeScale(animation.timeScale);

                this.activeTweens.push(timeLine);
            } else {
                for (let card of cards) {
                    TweenLite.set(card, {
                        rotationY: 0
                    });

                    this.fixIe(card, true);
                }

                this.updateHeaderText();

                if (this.footer.current) {
                    TweenLite.set(this.footer.current, {
                        autoAlpha: 1,
                    });
                }
            }
        }
    }

    componentWillUnmount(): void {
        for (let animation of this.activeTweens) {
            animation.kill();
        }
    }

    private renderCards() {
        const {isRevealed, cardCount, cardsWinRate, cardIndex} = this.props;

        const bonuses = this.random.shuffle(this.state.bonuses
            .concat(range(cardCount - Math.floor(cardCount * cardsWinRate)).map(() => BonusType.None)));

        const initialIndex = bonuses.indexOf(this.state.bonus) - cardIndex;

        return range(cardCount).map((i) => {
            let iconClassName = cssHelper(styles.card_front);

            let index = (initialIndex + i) % cardCount;
            if (index < 0) {
                index = cardCount + index;
            }

            const bonus = bonuses[index] || BonusType.None;

            if (bonus !== BonusType.None) {
                iconClassName.add(bonusStyles["bonus_icon_" + toCssClassName(BonusType[bonus])]);
            } else {
                iconClassName.add(styles.card_empty);
            }

            const isEnabled = !isRevealed && this.state.enabled;

            return (
                <div key={i} className={styles.card} onClick={isEnabled ? () => this.select(i) : undefined} data-index={i}>
                    <i className={iconClassName.toString()}/>
                    <i className={styles.card_back}/>
                </div>
            );
        });
    }

    private select(cardIndex: number) {
        const isWon = this.props.isFirstSelect || this.random.next() < this.props.cardsWinRate;

        if (isWon) {
            this.setState({
                bonus: this.state.bonuses[this.random.nextInt(this.state.bonuses.length)]
            });
        }

        this.props.dispatch(selectCard({
            cardIndex,
            isWon,
        }));
    }

    private close() {
        this.props.dispatch(leaveCardsGame({
            isAnswered: this.props.isRevealed,
            isRight: this.props.isWon,
            bonus: this.props.isWon ? this.state.bonus : undefined,
        }));
    }

    private reshuffleCards(prevCards: Card[], iteration = 1, amount = 5, timeScale = 1) {
        const reorderedCards: any = this.random.shuffle(prevCards).map((card, i) => {
            const transform = (prevCards[i].node as any)._gsTransform;

            return {
                x: prevCards[i].node.offsetLeft - card.node.offsetLeft + transform.x,
                y: prevCards[i].node.offsetTop - card.node.offsetTop + transform.y,
                rotation: transform.rotation,
                node: card.node
            };
        });

        for (let i = 0; i < reorderedCards.length; i++) {
            const tween = TweenLite.to(reorderedCards[i].node, 0.2, {
                x: reorderedCards[i].x,
                y: reorderedCards[i].y,
                rotation: reorderedCards[i].rotation,
                delay: 0.1 * iteration
            }).timeScale(timeScale);

            this.activeTweens.push(tween);
        }

        if (iteration < amount) {
            this.reshuffleCards(reorderedCards, iteration + 1, amount, timeScale);
        }
    };

    private getGameBonuses() {
        const {cardCount, cardsWinRate, cardBonuses} = this.props;

        const wonCount = Math.floor(cardCount * cardsWinRate);
        const bonusValues = Object.values(cardBonuses);

        return this.random.shuffle(bonusValues).slice(0, wonCount);
    }

    private fixIe(card: HTMLElement, flip: boolean) {
        if (!this.isIE)
            return;

        const front = card.getElementsByClassName(styles.card_front)[0];
        const back = card.getElementsByClassName(styles.card_back)[0];

        TweenLite.set(front, {
            rotationY: flip ? 0 : 180
        });

        TweenLite.set(back, {
            rotationY: flip ? 180 : 0
        });
    }

    private updateHeaderText() {
        const {isWon} = this.props;

        if (this.header.current) {
            let text;

            if (isWon) {
                text = i18n("gameplay.cardsminigame.wontext");
                playSound("bonus-win");
            } else {
                text = i18n("gameplay.cardsminigame.losttext");
                playSound("bonus-fail");
            }

            this.header.current.innerHTML = text;
            this.header.current.style.fontSize = getFontSize(text.length, 45, 70, 12, 30) + "px";
        }
    }
}

export default connect(({ game, miniGames: { cards }, settings }: IApplicationState): CardsMiniGameSceneProps => ({
    ...cards,
    levelScenery: game.levelScenery,
    cardCount: settings.miniGames.cardCount,
    cardsWinRate: settings.miniGames.cardsWinRate,
    cardBonuses: settings.miniGames.cardBonuses,
    randomSeed: settings.randomSeed ^ game.currentTileId,
    animation: settings.animation,
}))(CardsMiniGameScene);